import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner, BackLink } from '../components/PageChrome';

export default function RewardForm() {
  const { rewardId } = useParams();
  const location = useLocation();
  const isNew = location.pathname === '/rewards/new' || rewardId === 'new';
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '',
    description: '',
    code: '',
    expiryDate: '',
    discountType: 'PERCENT',
    discountValue: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const r = await adminApi.getReward(rewardId);
        if (cancelled) return;
        setForm({
          title: r.title || '',
          description: r.description || '',
          code: r.code || '',
          expiryDate: r.expiryDate ? r.expiryDate.slice(0, 16) : '',
          discountType: r.discountType || 'PERCENT',
          discountValue: r.discountValue != null ? String(r.discountValue) : '',
          isActive: Boolean(r.isActive),
        });
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, rewardId]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const expiryDate = form.expiryDate
        ? new Date(form.expiryDate).toISOString()
        : undefined;
      const body = {
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        code: form.code.trim().toUpperCase(),
        expiryDate,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        isActive: form.isActive,
      };
      if (isNew) {
        await adminApi.createReward(body);
      } else {
        await adminApi.updateReward(rewardId, body);
      }
      navigate('/rewards');
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <>
      <BackLink to="/rewards">Rewards</BackLink>
      <PageHeader title={isNew ? 'New reward' : 'Edit reward'} />
      <ErrorBanner message={error} />
      <form onSubmit={handleSubmit}>
        <Card className="mb-3">
          <CardBody>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Title</label>
                <input className="form-control" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Code</label>
                <input className="form-control" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Expires</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={form.expiryDate}
                  onChange={(e) => setForm((f) => ({ ...f, expiryDate: e.target.value }))}
                  required
                />
              </div>
              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Discount type</label>
                <select
                  className="form-select"
                  value={form.discountType}
                  onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value }))}
                >
                  <option value="PERCENT">Percent</option>
                  <option value="FIXED">Fixed amount</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Discount value</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={form.discountValue}
                  onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                  required
                />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <div className="form-check">
                  <input
                    id="rw-act"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="rw-act">
                    Active
                  </label>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/rewards')} disabled={saving}>
            Cancel
          </button>
        </div>
      </form>
    </>
  );
}
